import { Injectable } from '@angular/core';
import { getParamByISO } from "iso-country-currency";

export interface FMLocaleInfo {
  currency: string;
  country: string;
  locale: string;
  timezone: string;
}

@Injectable({
  providedIn: 'root'
})
export class FMLocaleDetectorService {

  private _currency: string = 'GBP';
  private _country: string = 'GB';
  private _locale: string = 'en_GB'; // Changed from 'en-GB' to 'en_GB'
  private _timezone: string = 'Europe/London';

  constructor() {
  }

  get localeInfo(): FMLocaleInfo {
    return {
      currency: this._currency,
      country: this._country,
      locale: this._locale,
      timezone: this._timezone
    };
  }

  async setLocale() {
    // Set initial defaults
    this._currency = 'GBP';
    this._country = 'GB';
    this._locale = 'en_GB'; // Changed from 'en-GB' to 'en_GB'
    this._timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Try browser locale first (most reliable and privacy-friendly)
    if (this.setLocaleFromBrowser()) {
      return;
    }

    // Use timezone-based detection as fallback (no permissions needed)
    this.setLocaleFromTimezone();

    console.log(`Locale detected: ${this._country} (${this._currency})`);
  }

  private setLocaleFromTimezone(): void {
    // Lightweight timezone-based country detection
    const timezone = this._timezone;

    // Common timezone to country/currency mappings
    // This can be expanded as needed
    const timezoneMap: Record<string, {country: string, currency: string}> = {
      // Europe
      'Europe/London': { country: 'GB', currency: 'GBP' },
      'Europe/Dublin': { country: 'IE', currency: 'EUR' },
      'Europe/Paris': { country: 'FR', currency: 'EUR' },
      'Europe/Berlin': { country: 'DE', currency: 'EUR' },
      'Europe/Madrid': { country: 'ES', currency: 'EUR' },
      'Europe/Rome': { country: 'IT', currency: 'EUR' },
      'Europe/Amsterdam': { country: 'NL', currency: 'EUR' },
      'Europe/Brussels': { country: 'BE', currency: 'EUR' },
      'Europe/Zurich': { country: 'CH', currency: 'CHF' },
      'Europe/Stockholm': { country: 'SE', currency: 'SEK' },
      'Europe/Oslo': { country: 'NO', currency: 'NOK' },
      'Europe/Copenhagen': { country: 'DK', currency: 'DKK' },
      'Europe/Warsaw': { country: 'PL', currency: 'PLN' },
      'Europe/Athens': { country: 'GR', currency: 'EUR' },
      'Europe/Istanbul': { country: 'TR', currency: 'TRY' },

      // Americas
      'America/New_York': { country: 'US', currency: 'USD' },
      'America/Chicago': { country: 'US', currency: 'USD' },
      'America/Denver': { country: 'US', currency: 'USD' },
      'America/Los_Angeles': { country: 'US', currency: 'USD' },
      'America/Toronto': { country: 'CA', currency: 'CAD' },
      'America/Vancouver': { country: 'CA', currency: 'CAD' },
      'America/Mexico_City': { country: 'MX', currency: 'MXN' },
      'America/Sao_Paulo': { country: 'BR', currency: 'BRL' },
      'America/Argentina/Buenos_Aires': { country: 'AR', currency: 'ARS' },

      // Asia/Pacific
      'Asia/Tokyo': { country: 'JP', currency: 'JPY' },
      'Asia/Seoul': { country: 'KR', currency: 'KRW' },
      'Asia/Shanghai': { country: 'CN', currency: 'CNY' },
      'Asia/Hong_Kong': { country: 'HK', currency: 'HKD' },
      'Asia/Singapore': { country: 'SG', currency: 'SGD' },
      'Asia/Kolkata': { country: 'IN', currency: 'INR' },
      'Australia/Sydney': { country: 'AU', currency: 'AUD' },
      'Australia/Melbourne': { country: 'AU', currency: 'AUD' },
      'Pacific/Auckland': { country: 'NZ', currency: 'NZD' }
    };

    const match = timezoneMap[timezone];
    if (match) {
      this._country = match.country;
      this._currency = match.currency;
      // Set locale using underscore format for CyberSource compatibility
      this._locale = this.getLanguageCodeFromCountry(match.country) + '_' + match.country;
      console.log(`Locale detected from timezone: ${this._country} (${this._currency})`);
      return;
    }

    // For unmapped timezones, try to extract region information
    const region = timezone.split('/');
    if (region.length > 0) {
      let country = '';
      let currency = '';

      switch (region[0]) {
        case 'Europe':
          country = 'DE';
          currency = 'EUR';
          break;
        case 'America':
          country = 'US';
          currency = 'USD';
          break;
        case 'Asia':
          country = 'JP';
          currency = 'JPY';
          break;
        case 'Australia':
        case 'Pacific':
          country = 'AU';
          currency = 'AUD';
          break;
        case 'Africa':
          country = 'ZA';
          currency = 'ZAR';
          break;
      }

      if (country) {
        this._country = country;
        this._currency = currency;
        this._locale = this.getLanguageCodeFromCountry(country) + '_' + country;
        console.log(`Locale approximated from timezone region: ${this._country} (${this._currency})`);
      }
    }
  }

  private setLocaleFromBrowser() {
    try {
      const browserLocale = navigator.language || navigator.languages[0];
      if (!browserLocale) return false;

      const countryCode = this.extractCountryFromLocale(browserLocale);
      if (countryCode) {
        const currency = getParamByISO(countryCode, 'currency');
        if (currency) {
          this._country = countryCode;
          this._currency = currency;
          // Convert browser locale format (en-GB) to CyberSource format (en_GB)
          this._locale = browserLocale.replace('-', '_');
          console.log(`Locale detected from browser: ${this._country} (${this._currency})`);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Browser locale detection failed, using defaults');
      return false;
    }
  }

  private extractCountryFromLocale(locale: string): string | null {
    const parts = locale.split('-');
    return parts.length > 1 ? parts[1].toUpperCase() : null;
  }

  // Helper method to get language code from country code
  private getLanguageCodeFromCountry(countryCode: string): string {
    const countryToLanguageMap: Record<string, string> = {
      'GB': 'en', 'US': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en',
      'FR': 'fr', 'BE': 'fr', 'CH': 'fr',
      'DE': 'de', 'AT': 'de',
      'ES': 'es', 'MX': 'es', 'AR': 'es',
      'IT': 'it',
      'NL': 'nl',
      'PT': 'pt', 'BR': 'pt',
      'RU': 'ru',
      'CN': 'zh', 'HK': 'zh',
      'JP': 'ja',
      'KR': 'ko',
      'IN': 'en', // Default to English for India
      'SG': 'en', // Default to English for Singapore
      'SE': 'sv',
      'NO': 'no',
      'DK': 'da',
      'PL': 'pl',
      'GR': 'el',
      'TR': 'tr',
      'ZA': 'en' // Default to English for South Africa
    };

    return countryToLanguageMap[countryCode] || 'en'; // Default to English
  }
}
